import {applyOperation} from '@github-ui/optimistic-connection'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useQueryClient} from '@github-ui/react-query'
import type {MilestoneState} from '@github-ui/server-persisted-queries/types/RepositoryMilestoneIndexPageQuery'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useMemo} from 'react'

import {buildToggleMilestoneOperation, MILESTONES_CONNECTION_PATH} from './utils/milestone-connection-operations'

type UseToggleMilestoneStateOptions = {
  repositoryId: string
  milestoneId: string
  milestoneNumber: number
  nameWithOwner: string
  /** The milestone's state *before* the toggle; the target state is its opposite. */
  fromState: MilestoneState
  /** The data-router index route query key, so the confirmed removal can be folded into base. */
  queryKey?: RouteQueryKey
  onError?: (error: Error) => void
  onSuccess?: () => void
}

/**
 * Optimistically closes/reopens a milestone via `@github-ui/optimistic-connection`: the row leaves the
 * list and both counts shift while the PUT is in flight, rolling back on error and folding on success.
 */
export function useToggleMilestoneStateMutation({
  repositoryId,
  milestoneId,
  milestoneNumber,
  nameWithOwner,
  fromState,
  queryKey,
  onError,
  onSuccess,
}: UseToggleMilestoneStateOptions) {
  const queryClient = useQueryClient()
  const targetState: MilestoneState = fromState === 'CLOSED' ? 'OPEN' : 'CLOSED'
  const operation = useMemo(() => buildToggleMilestoneOperation(milestoneId, fromState), [milestoneId, fromState])

  const {mutate} = useConnectionMutation<void, Error>({
    ownerId: repositoryId,
    connectionPath: MILESTONES_CONNECTION_PATH,
    mutationFn: async () => {
      let response: Response
      try {
        response = await reactFetchJSON(`/${nameWithOwner}/milestones/${milestoneNumber}/state`, {
          method: 'PUT',
          body: {state: targetState},
        })
      } catch {
        throw new Error('Could not change Milestone state')
      }

      if (!response.ok) {
        throw new Error('Could not change Milestone state')
      }

      const result = await response.json()
      const errors = result.data?.updateMilestone?.errors
      if (!result.data?.updateMilestone?.milestone || (errors && errors.length > 0)) {
        throw new Error('Could not change Milestone state')
      }
    },
    onError,
    onSuccess: () => {
      // Cancel any in-flight revalidation before folding: a stale-while-revalidate fetch started before
      // the mutation could otherwise resolve afterward and restore the row.
      if (queryKey) {
        void queryClient.cancelQueries({queryKey})
        queryClient.setQueryData(queryKey, base => (base ? applyOperation(base, repositoryId, operation) : base))
      }
      onSuccess?.()
    },
  })

  return useCallback(() => {
    mutate({operation})
  }, [mutate, operation])
}
